import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { money, dateFmt, statusMeta, hasRole, formatErr } from "@/lib/format";
import { StatusBadge } from "@/components/StatusBadge";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { Loader2, Upload, ScanLine, Search, Archive, ArchiveRestore } from "lucide-react";

const STATUSES = ["uploaded", "awaiting_review", "in_review", "matched", "posted"];

export default function Invoices() {
    const navigate = useNavigate();
    const { user } = useAuth();
    const canArchive = hasRole(user, ["school_admin", "purchasing_admin"]);
    const [rows, setRows] = useState(null);
    const [pos, setPos] = useState([]);
    const [open, setOpen] = useState(false);
    const [file, setFile] = useState(null);
    const [poId, setPoId] = useState("none");
    const [busy, setBusy] = useState(false);
    const [q, setQ] = useState("");
    const [status, setStatus] = useState("all");
    const [archived, setArchived] = useState(false);

    const load = () => {
        const p = new URLSearchParams();
        if (q) p.set("q", q);
        if (status !== "all") p.set("status", status);
        if (archived) p.set("archived", "true");
        api.get(`/invoices?${p.toString()}`).then((r) => setRows(r.data)).catch(() => setRows([]));
    };
    useEffect(() => { const t = setTimeout(load, 300); return () => clearTimeout(t); }, [q, status, archived]);
    useEffect(() => { api.get("/purchase-orders").then((r) => setPos(r.data)).catch(() => {}); }, []);

    const upload = async () => {
        if (!file) { toast.error("Choose a file"); return; }
        setBusy(true);
        try {
            const fd = new FormData();
            fd.append("file", file);
            if (poId !== "none") fd.append("po_id", poId);
            const { data } = await api.post("/invoices/upload", fd);
            toast.success("Invoice uploaded");
            setOpen(false);
            navigate(`/app/invoices/${data.id}`);
        } catch (err) { toast.error(formatErr(err.response?.data?.detail)); } finally { setBusy(false); }
    };
    const toggleArchive = async (inv, e) => {
        e.stopPropagation();
        try { await api.post(`/invoices/${inv.id}/archive`, { archived: !archived }); toast.success(archived ? "Restored" : "Archived"); load(); }
        catch (err) { toast.error(formatErr(err.response?.data?.detail)); }
    };

    return (
        <div className="space-y-6 pe-fade-up">
            <div className="flex flex-wrap items-end justify-between gap-3">
                <div>
                    <h1 className="text-2xl sm:text-3xl font-semibold tracking-tight text-brand-emerald">Invoices & OCR</h1>
                    <p className="text-muted-foreground text-sm mt-1">Scan vendor invoices, review line items, and post to Veracross.</p>
                </div>
                <Dialog open={open} onOpenChange={setOpen}>
                    <DialogTrigger asChild>
                        <Button className="bg-brand-emerald hover:bg-brand-emeraldhover rounded-lg" data-testid="upload-invoice-button">
                            <Upload className="h-4 w-4 mr-1" /> Upload Invoice
                        </Button>
                    </DialogTrigger>
                    <DialogContent>
                        <DialogHeader><DialogTitle>Upload a vendor invoice</DialogTitle></DialogHeader>
                        <div className="space-y-4">
                            <div>
                                <Label>Invoice file (PDF or image)</Label>
                                <Input type="file" accept=".pdf,.png,.jpg,.jpeg,.webp" onChange={(e) => setFile(e.target.files[0])} className="mt-1.5" data-testid="invoice-file-input" />
                            </div>
                            <div>
                                <Label>Link to Purchase Order (optional)</Label>
                                <Select value={poId} onValueChange={setPoId}>
                                    <SelectTrigger className="mt-1.5" data-testid="invoice-po-select"><SelectValue placeholder="No PO" /></SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="none">No PO / standalone</SelectItem>
                                        {pos.map((po) => (
                                            <SelectItem key={po.id} value={po.id} className="font-mono text-xs">{po.po_number} · {po.vendor_name} · {money(po.total)}</SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </div>
                        </div>
                        <DialogFooter>
                            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
                            <Button disabled={busy} onClick={upload} className="bg-brand-emerald hover:bg-brand-emeraldhover" data-testid="confirm-upload-button">
                                {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Upload"}
                            </Button>
                        </DialogFooter>
                    </DialogContent>
                </Dialog>
            </div>

            <div className="flex flex-wrap items-center gap-3">
                <div className="relative flex-1 min-w-[200px]">
                    <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                    <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search invoice #, vendor or PO" className="pl-9" data-testid="invoice-search" />
                </div>
                <Select value={status} onValueChange={setStatus}>
                    <SelectTrigger className="w-[180px]" data-testid="invoice-status-filter"><SelectValue placeholder="All statuses" /></SelectTrigger>
                    <SelectContent>
                        <SelectItem value="all">All statuses</SelectItem>
                        {STATUSES.map((s) => <SelectItem key={s} value={s}>{statusMeta(s).label}</SelectItem>)}
                    </SelectContent>
                </Select>
                {canArchive && (
                    <Button variant={archived ? "default" : "outline"} className="rounded-lg" onClick={() => setArchived((v) => !v)} data-testid="invoice-archived-toggle">
                        <Archive className="h-4 w-4 mr-1" /> {archived ? "Viewing archived" : "Show archived"}
                    </Button>
                )}
            </div>

            <div className="bg-white border border-border rounded-xl overflow-hidden">
                {rows === null ? (
                    <div className="grid place-items-center py-16 text-brand-emerald"><Loader2 className="h-6 w-6 animate-spin" /></div>
                ) : rows.length === 0 ? (
                    <div className="py-16 text-center text-muted-foreground"><ScanLine className="h-8 w-8 mx-auto mb-3 opacity-40" /> No invoices found.</div>
                ) : (
                    <div className="overflow-x-auto pe-scroll">
                        <table className="w-full text-sm" data-testid="invoice-table">
                            <thead><tr className="text-left text-muted-foreground border-b border-border bg-secondary/40">
                                <th className="p-3 font-medium">Invoice #</th>
                                <th className="p-3 font-medium">Vendor</th>
                                <th className="p-3 font-medium">PO</th>
                                <th className="p-3 font-medium">Date</th>
                                <th className="p-3 font-medium text-right">Total</th>
                                <th className="p-3 font-medium">Status</th>
                                {canArchive && <th className="p-3 font-medium text-right">Actions</th>}
                            </tr></thead>
                            <tbody>
                                {rows.map((inv) => (
                                    <tr key={inv.id} className="border-b border-border last:border-0 hover:bg-secondary/40 cursor-pointer" onClick={() => navigate(`/app/invoices/${inv.id}`)} data-testid={`invoice-row-${inv.id}`}>
                                        <td className="p-3 font-mono text-brand-teal">{inv.invoice_number || <span className="text-muted-foreground italic">unscanned</span>}</td>
                                        <td className="p-3">{inv.vendor_name || "—"}</td>
                                        <td className="p-3 font-mono text-xs text-muted-foreground">{inv.po_number || "—"}</td>
                                        <td className="p-3 text-muted-foreground">{dateFmt(inv.created_at)}</td>
                                        <td className="p-3 text-right font-mono">{money(inv.total)}</td>
                                        <td className="p-3"><StatusBadge status={inv.status} /></td>
                                        {canArchive && (
                                            <td className="p-3 text-right">
                                                <Button variant="ghost" size="sm" onClick={(e) => toggleArchive(inv, e)} data-testid={`archive-invoice-${inv.id}`}>
                                                    {archived ? <ArchiveRestore className="h-4 w-4" /> : <Archive className="h-4 w-4" />}
                                                </Button>
                                            </td>
                                        )}
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>
        </div>
    );
}
